import { clock, setClock, setSpeedMultiplier } from "../stores/clock"
import { getScenarioState } from "../stores/scenario"
import type { ClockAnnotation, SpeedControl } from "../types"
import { secondsToSimTime, simTimeToSeconds } from "../types"
import { playAmbient, stopAmbient } from "./ambient-audio"
import { agentEffectiveEnd, computeAgentPosition } from "./geometry"
import {
    checkTimelineCues,
    markPastCuesAsTriggered,
    pauseTimelineCues,
    resetTimelineCues,
    resumeTimelineCues,
} from "./timeline-audio"

/** Get the active speed multiplier from the unified scenario store */
function getActiveSpeedMultiplierFromStore(simSeconds: number): number {
    const controls = getScenarioState().speedControls
    if (controls.length === 0) return 2
    let active: SpeedControl | null = null
    for (const c of controls) {
        const cs = simTimeToSeconds(c.time)
        if (cs <= simSeconds) active = c
        else break
    }
    return active ? active.speedMultiplier : 2
}

export interface AgentPosition {
    agentId: string
    position: [number, number] | null
    finished: boolean
    visible: boolean
    hideLabel: boolean
}

let animFrameId: number | null = null
let startWallTime: number | null = null
let startSimSeconds: number | null = null
let currentSpeedMultiplier: number = 1
let manualSpeedOverride: boolean = false

let onPositionUpdate: ((positions: AgentPosition[]) => void) | null = null
let onClockAnnotation: ((annotation: ClockAnnotation) => void) | null = null
let onClearPopups: (() => void) | null = null
let onClearAgentMarkers: (() => void) | null = null
let onCollisionDetection: (() => void) | null = null
let onEndTimeReached: (() => void) | null = null
let onResetView: (() => void) | null = null

let shownAnnotations = new Set<string>()
let lastCheckedTime: number = -1
let lastCollisionCheck: number = 0
let endTimeTriggered: boolean = false
const BASE_COLLISION_INTERVAL = 2000 // 2 seconds at 1x speed

// Pause animation when tab loses focus to prevent time jumps and message replays
if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
        if (document.hidden && clock.isRunning) {
            pauseAnimation()
        }
    })
}

export function setPositionUpdateCallback(cb: (positions: AgentPosition[]) => void) {
    onPositionUpdate = cb
}

export function setClockAnnotationCallback(cb: (annotation: ClockAnnotation) => void) {
    onClockAnnotation = cb
}

export function setClearPopupsCallback(cb: () => void) {
    onClearPopups = cb
}

export function setResetViewCallback(cb: () => void) {
    onResetView = cb
}

export function setClearAgentMarkersCallback(cb: () => void) {
    onClearAgentMarkers = cb
}

export function setCollisionDetectionCallback(cb: () => void) {
    onCollisionDetection = cb
}

export function setEndTimeReachedCallback(cb: () => void) {
    onEndTimeReached = cb
}

function computePositions(simSeconds: number): AgentPosition[] {
    const scenario = getScenarioState()
    return scenario.agents.map((agent) => {
        const result = computeAgentPosition(agent, simSeconds)
        const endSec = agentEffectiveEnd(agent)
        const secsLeft = endSec - simSeconds
        const hideLabel = result.finished || (secsLeft >= 0 && secsLeft <= 3)
        return {
            agentId: agent.id,
            position: result.position,
            finished: result.finished,
            visible: result.visible,
            hideLabel,
        }
    })
}

function checkClockAnnotations(simSeconds: number) {
    if (!onClockAnnotation) return

    const scenario = getScenarioState()

    // Suppress global messages at high speeds (>10x) to prevent UI clogging
    // But still mark them as shown so they don't appear when speed drops
    if (clock.speedMultiplier > 10) {
        for (const ann of scenario.clockAnnotations) {
            if (shownAnnotations.has(ann.id)) continue
            const annSec = simTimeToSeconds(ann.time)
            if (simSeconds >= annSec && ann.text.trim()) {
                shownAnnotations.add(ann.id) // Mark as shown without displaying
            }
        }
        lastCheckedTime = simSeconds
        return
    }

    // Detect if time jumped backward (skip back, clock change, etc.)
    const timeJumpedBackward = lastCheckedTime >= 0 && simSeconds < lastCheckedTime

    // Detect if time jumped forward significantly (skip forward, clock change)
    const timeJumpedForward = lastCheckedTime >= 0 && simSeconds > lastCheckedTime + 2

    if (timeJumpedBackward || timeJumpedForward) {
        // Time changed non-linearly - clear shown annotations and show only the most recent one
        shownAnnotations.clear()

        // Clear any currently displayed popups
        if (onClearPopups) {
            onClearPopups()
        }

        // Find the most recent annotation that should be active at current time
        let mostRecentAnn: ClockAnnotation | null = null
        let mostRecentTime = -1

        for (const ann of scenario.clockAnnotations) {
            const annSec = simTimeToSeconds(ann.time)
            if (annSec <= simSeconds && ann.text.trim() && annSec > mostRecentTime) {
                mostRecentTime = annSec
                mostRecentAnn = ann
            }
        }

        // Show only the most recent annotation if found
        if (mostRecentAnn) {
            shownAnnotations.add(mostRecentAnn.id)
            onClockAnnotation(mostRecentAnn)
        }
    } else {
        // Normal forward progression - show any new annotations as we pass them
        for (const ann of scenario.clockAnnotations) {
            if (shownAnnotations.has(ann.id)) continue
            const annSec = simTimeToSeconds(ann.time)
            if (simSeconds >= annSec && ann.text.trim()) {
                shownAnnotations.add(ann.id)
                onClockAnnotation(ann)
            }
        }
    }

    lastCheckedTime = simSeconds
}

function tick(timestamp: number) {
    if (!clock.isRunning) {
        animFrameId = null
        return
    }

    if (startWallTime === null) {
        startWallTime = timestamp
        startSimSeconds = simTimeToSeconds(clock.currentTime)
        currentSpeedMultiplier = clock.speedMultiplier
    }

    const wallElapsed = (timestamp - startWallTime) / 1000
    const simElapsed = wallElapsed * currentSpeedMultiplier
    const currentSimSec = startSimSeconds! + simElapsed
    const clampedSec = Math.min(currentSimSec, 86400)

    // Only apply automatic speed changes if user hasn't manually overridden
    if (!manualSpeedOverride) {
        const activeSpeed = getActiveSpeedMultiplierFromStore(clampedSec)

        // Update clock.speedMultiplier if it differs from active speed
        if (clock.speedMultiplier !== activeSpeed) {
            setSpeedMultiplier(activeSpeed)
        }
    }

    // If speed changed (manual or automatic), re-anchor so position doesn't jump
    if (clock.speedMultiplier !== currentSpeedMultiplier) {
        startSimSeconds = clampedSec
        startWallTime = timestamp
        currentSpeedMultiplier = clock.speedMultiplier
        // Update lastCheckedTime to prevent annotations from replaying when speed changes
        lastCheckedTime = clampedSec
    }

    setClock("currentTime", secondsToSimTime(clampedSec))

    const positions = computePositions(clampedSec)
    onPositionUpdate?.(positions)
    checkClockAnnotations(clampedSec)
    checkTimelineCues(clampedSec)

    // Collision detection: check every (2 seconds / speed) to reduce overlap
    const collisionInterval = BASE_COLLISION_INTERVAL / Math.max(1, clock.speedMultiplier)
    if (timestamp - lastCollisionCheck > collisionInterval) {
        onCollisionDetection?.()
        lastCollisionCheck = timestamp
    }

    // Check if endTime is reached (if configured)
    const scenario = getScenarioState()
    if (scenario.meta.endTime && !endTimeTriggered) {
        const endTimeSeconds = simTimeToSeconds(scenario.meta.endTime)
        if (clampedSec >= endTimeSeconds) {
            endTimeTriggered = true

            // Trigger analysis modal only if showAnalysisAtEnd is enabled AND analysis has content
            const hasAnalysisContent = scenario.analysis.sections.length > 0
            const sessionKey = `sim86_analysis_shown_${scenario.meta.name}`
            const alreadyShownThisSession = sessionStorage.getItem(sessionKey) === "1"
            const shouldShowModal = scenario.meta.showAnalysisAtEnd && hasAnalysisContent && onEndTimeReached && !alreadyShownThisSession

            if (shouldShowModal) {
                // Stop and show modal — first time only
                setClock("isRunning", false)
                animFrameId = null
                sessionStorage.setItem(sessionKey, "1")
                onEndTimeReached!()
                return
            }
            // Modal already shown this session — let animation continue past endTime
        }
    }

    if (clampedSec >= 86400) {
        setClock("isRunning", false)
        animFrameId = null
        return
    }

    animFrameId = requestAnimationFrame(tick)
}

export function startAnimation() {
    if (animFrameId !== null) return
    startWallTime = null
    startSimSeconds = null
    shownAnnotations.clear()
    lastCollisionCheck = 0 // Reset collision check timer
    endTimeTriggered = false // Reset endTime trigger

    // Reset manual override flag when starting
    manualSpeedOverride = false

    // Set initial speed based on current time and speed controls (from unified store)
    const currentSeconds = simTimeToSeconds(clock.currentTime)
    const initialSpeed = getActiveSpeedMultiplierFromStore(currentSeconds)
    setSpeedMultiplier(initialSpeed)

    // Clear any existing popups when starting
    if (onClearPopups) {
        onClearPopups()
    }

    // Mark all past annotations as already shown to prevent replay
    const scenario = getScenarioState()
    for (const ann of scenario.clockAnnotations) {
        const annSec = simTimeToSeconds(ann.time)
        if (annSec <= currentSeconds) {
            shownAnnotations.add(ann.id)
        }
    }

    // Set lastCheckedTime to current time to prevent replays
    lastCheckedTime = currentSeconds

    // Mark past audio cues as already triggered
    markPastCuesAsTriggered(currentSeconds)

    setClock("isRunning", true)
    playAmbient()
    resumeTimelineCues()
    animFrameId = requestAnimationFrame(tick)
}

export function pauseAnimation() {
    setClock("isRunning", false)
    if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId)
        animFrameId = null
    }
    startWallTime = null
    startSimSeconds = null
    manualSpeedOverride = false
    stopAmbient()
    pauseTimelineCues()
}

export function resetAnimation() {
    pauseAnimation()
    shownAnnotations.clear()
    lastCheckedTime = -1
    stopAmbient()
    resetTimelineCues()

    // Clear popups
    if (onClearPopups) {
        onClearPopups()
    }

    // Clear agent markers from map
    if (onClearAgentMarkers) {
        onClearAgentMarkers()
    }

    // NOTE: Viewport pins are scenario data, not animation state.
    // They should NOT be cleared on reset - they persist across restarts.

    // Reset clock to scenario start time
    const scenario = getScenarioState()
    setClock({
        currentTime: { ...scenario.meta.startTime },
        speedMultiplier: 2,
        isRunning: false,
    })

    onPositionUpdate?.(
        scenario.agents.map((a) => ({
            agentId: a.id,
            position: null,
            finished: false,
            visible: false,
            hideLabel: false,
        }))
    )

    onResetView?.()
}

/** Enable manual speed override - allows user to override automatic speed controls */
export function enableManualSpeedOverride() {
    manualSpeedOverride = true
}

/** Skip forward/back by seconds — re-anchors the animation */
export function skipSeconds(delta: number) {
    const cur = simTimeToSeconds(clock.currentTime)
    const next = Math.max(0, Math.min(cur + delta, 86399))
    setClock("currentTime", secondsToSimTime(next))

    // Reset endTime trigger when seeking
    endTimeTriggered = false

    // Clear popups immediately
    if (onClearPopups) {
        onClearPopups()
    }

    // Clear shown annotations so they don't get replayed
    shownAnnotations.clear()

    // Mark all past annotations as already shown
    const scenario = getScenarioState()
    for (const ann of scenario.clockAnnotations) {
        const annSec = simTimeToSeconds(ann.time)
        if (annSec < next) {
            // Changed from <= to < to exclude exact timestamp
            shownAnnotations.add(ann.id)
        }
    }

    // Update lastCheckedTime to prevent jump detection from showing past messages
    lastCheckedTime = next

    // Re-anchor so the running animation picks up from here
    startWallTime = null
    startSimSeconds = null

    // Reset timeline cues so skipping back re-triggers them correctly
    resetTimelineCues()

    // Mark past audio cues as triggered to prevent replay
    markPastCuesAsTriggered(next)
}
